/**
 * Corrective shape key discovery and weight computation — Pose-Space Deformation.
 *
 * Corrective shape keys fix mesh deformation artifacts at extreme joint angles
 * (e.g., elbow/knee creasing). They are driven automatically by joint rotation —
 * no manual control needed.
 *
 * ## Naming convention
 *
 * Artists sculpt unsided shape keys with corrections on both halves:
 *
 *   corr_{joint}_{action}          (in the source .blend file)
 *
 * Examples:  corr_shin_backward, corr_shoulder_forward, corr_hips_backward
 *
 * The build script (build-mannequin.py) automatically splits each into
 * left and right variants by masking vertex deltas on the X axis:
 *
 *   corr_{joint}_{side}_{action}              (in the GLB)
 *
 * Examples:  corr_shin_l_backward, corr_shoulder_r_forward
 *
 * Multi-segment correctives add keyframe angle suffixes:
 *
 *   corr_{joint}_{side}_{action}_k{angle}     (intermediate keyframe)
 *
 * Examples:  corr_thigh_l_backward_k65
 *
 * Each body type variant (neutral, muscular, skinny) has its own GLB with
 * its own corrective shape keys — no cross-variant blending at runtime.
 *
 * ## Joint → bone mapping
 *
 * The mapping from joint/action names to skeleton bone + target quaternion is
 * loaded from correctiveBindings.json, which the Blender plugin exports
 * directly. To add a new corrective, sculpt it in the plugin and re-export —
 * no TypeScript changes needed.
 *
 * ## Activation curve (Swing-Twist Pose-Space Deformation)
 *
 * Weights are driven by the bone's rotation around the target's axis, measured
 * via swing-twist decomposition. This matches the Blender addon's approach of
 * decomposing the bone's local quaternion to Euler angles and extracting the
 * dominant axis rotation:
 *
 *   axis = normalize(targetRotation.xyz)     // target's rotation axis
 *   twistAngle = swingTwistDecompose(delta, axis).twist
 *   progressAngle = (twistAngle / targetTwistAngle) * activationAngle
 *   weight = clamp(progressAngle / keyframeAngle, 0, 1)
 *
 * This provides:
 * - Axis-aligned progress measurement matching the addon's parameterization
 * - Correct tent-function placement for multi-keyframe correctives
 * - Implicit direction gating (opposite rotations give negative twist → 0)
 * - Bilateral mirroring via quaternion mirror instead of Z-flip
 *
 * For multi-keyframe correctives, piecewise-linear interpolation is used
 * between adjacent keyframes (parameterized by progressAngle).
 */

import type * as THREE from "three/webgpu";
import bindingsJson from "./correctiveBindings.json";
import {
  mirrorQuaternion,
  type Quat4,
  quaternionAngle,
} from "./quaternionMath.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Target rotation for a secondary bone in a combo corrective. */
export interface SecondaryBoneTarget {
  boneName: string;
  targetRotation: Quat4;
  activationAngle: number;
}

/** A single corrective shape key binding, auto-discovered from the GLB. */
export interface CorrectiveBinding {
  /** Morph target name in the GLB (e.g., "corr_shin_l_backward") */
  morphName: string;
  /** Index in morphTargetInfluences */
  morphIndex: number;
  /** Bone whose rotation drives this shape key */
  boneName: string;
  /** Delta quaternion [x,y,z,w] at which this corrective is fully active */
  targetRotation: Quat4;
  /**
   * Precomputed rotation angle (radians) from identity to targetRotation.
   * This is the distance at which weight transitions from 0 (at rest) to 1
   * (at the target pose).
   */
  activationAngle: number;
  /**
   * Keyframe angle (radians) — the rotation angle from rest that this
   * specific keyframe corrects at. For single-keyframe correctives, equals
   * activationAngle. For multi-keyframe, this is the intermediate angle.
   */
  keyframeAngle: number;
  /**
   * Group key for grouping multi-keyframe bindings. Bindings with the same
   * groupKey share the same corrective group and use piecewise interpolation.
   * Format: "joint:side:action"
   */
  groupKey: string;
  /** For combo correctives: additional bone targets (product activation). */
  secondaryBones?: SecondaryBoneTarget[];
}

/** A group of corrective bindings sharing the same joint/action/side. */
interface CorrectiveGroup {
  boneName: string;
  /** Full-activation target quaternion [x,y,z,w] */
  targetRotation: Quat4;
  /** Precomputed activation angle (radians) */
  activationAngle: number;
  /** Keyframes sorted by angle, each with morphIndex and angle (radians) */
  keyframes: Array<{ morphIndex: number; angle: number }>;
  /** For combo correctives: additional bone targets (product activation). */
  secondaryBones?: SecondaryBoneTarget[];
}

// ---------------------------------------------------------------------------
// Joint → bone mapping (loaded from correctiveBindings.json)
// ---------------------------------------------------------------------------

function deg(d: number): number {
  return d * (Math.PI / 180);
}

/**
 * Runtime representation of a joint/action definition.
 * Built from the JSON at module load time.
 */
interface JointActionDef {
  bone: string;
  rotation: Quat4;
  activationAngle: number;
  unsided?: boolean;
}

/** JSON shape for a single entry in correctiveBindings.json */
interface BindingJsonEntry {
  bone: string;
  rotation: [number, number, number, number];
  unsided?: boolean;
}

/** JSON shape for a combo entry in correctiveBindings.json._combos */
interface ComboJsonEntry {
  bones: Array<{
    bone: string;
    rotation: [number, number, number, number];
    unsided?: boolean;
  }>;
}

/**
 * Joint → action → bone/rotation mapping, built from the JSON file
 * that the Blender plugin exports.
 */
const JOINT_ACTION_MAP: Record<string, Record<string, JointActionDef>> = {};

/** Combo corrective definitions from correctiveBindings.json._combos */
interface ComboDef {
  bones: Array<{
    bone: string;
    rotation: Quat4;
    activationAngle: number;
    unsided?: boolean;
  }>;
}
const COMBO_MAP: Record<string, ComboDef> = {};

// Build the maps from JSON at module load time (synchronous, no I/O)
for (const [joint, actions] of Object.entries(
  bindingsJson as unknown as Record<
    string,
    Record<string, BindingJsonEntry | ComboJsonEntry>
  >,
)) {
  if (joint === "_combos") {
    // Parse combo entries
    for (const [comboKey, comboEntry] of Object.entries(
      actions as unknown as Record<string, ComboJsonEntry>,
    )) {
      COMBO_MAP[comboKey] = {
        bones: comboEntry.bones.map((b) => ({
          bone: b.bone,
          rotation: [
            b.rotation[0],
            b.rotation[1],
            b.rotation[2],
            b.rotation[3],
          ] as Quat4,
          activationAngle: quaternionAngle(
            b.rotation[0],
            b.rotation[1],
            b.rotation[2],
            b.rotation[3],
            0,
            0,
            0,
            1,
          ),
          unsided: b.unsided,
        })),
      };
    }
    continue;
  }
  JOINT_ACTION_MAP[joint] = {};
  for (const [action, entry] of Object.entries(
    actions as Record<string, BindingJsonEntry>,
  )) {
    const r = entry.rotation;
    JOINT_ACTION_MAP[joint][action] = {
      bone: entry.bone,
      rotation: [r[0], r[1], r[2], r[3]],
      activationAngle: quaternionAngle(r[0], r[1], r[2], r[3], 0, 0, 0, 1),
      unsided: entry.unsided,
    };
  }
}

/** Side suffix in shape key name → bone name suffix in skeleton */
const SIDE_MAP: Record<string, string> = { l: "L", r: "R" };

// ---------------------------------------------------------------------------
// Runtime bindings — mutable registry for correctives authored in the editor
// ---------------------------------------------------------------------------

/**
 * Runtime bindings added by the corrective shape key editor.
 * Merged into discoverCorrectiveBindings() results.
 */
const _runtimeBindings: CorrectiveBinding[] = [];

/**
 * Add a runtime corrective binding. Called when a new corrective is saved
 * in the web editor. The binding will be included in subsequent calls to
 * discoverCorrectiveBindings().
 */
export function addRuntimeBinding(binding: CorrectiveBinding): void {
  // Remove any existing binding with the same morph name (re-save/edit)
  const existing = _runtimeBindings.findIndex(
    (b) => b.morphName === binding.morphName,
  );
  if (existing >= 0) _runtimeBindings.splice(existing, 1);
  _runtimeBindings.push(binding);
}

/**
 * Remove a runtime corrective binding by morph name.
 */
export function removeRuntimeBinding(morphName: string): void {
  const idx = _runtimeBindings.findIndex((b) => b.morphName === morphName);
  if (idx >= 0) {
    _runtimeBindings.splice(idx, 1);
  }
}

/**
 * Clear all runtime bindings (e.g., on project reset).
 */
export function clearRuntimeBindings(): void {
  _runtimeBindings.length = 0;
}

/**
 * Construct the full bone name from a base name and side suffix.
 * Handles dotted multi-segment names (e.g., finger bones).
 * Three.js strips dots from bone names on GLB import, so we produce
 * the dotless convention that matches the live skeleton:
 *   "shin" + "L" → "shinL"
 *   "finger_index.1" + "L" → "finger_indexL1"
 */
function makeBoneName(base: string, side: string): string {
  // Three.js strips dots from bone names on GLB import, so we must match
  // the dotless convention: "upper_arm" + "L" → "upper_armL",
  // "finger_index.1" + "L" → "finger_indexL1"
  const dotIdx = base.lastIndexOf(".");
  if (dotIdx >= 0) {
    return base.slice(0, dotIdx) + side + base.slice(dotIdx + 1);
  }
  return base + side;
}

// ---------------------------------------------------------------------------
// Discovery
// ---------------------------------------------------------------------------

/** Matches: corr_{joint}_{side}_{action} with optional _k{angle}.
 *  Joint and action may contain digits (e.g., fingerindex1). */
const CORR_REGEX = /^corr_([a-z0-9]+)_([lr])_([a-z0-9]+?)(?:_k(\d+))?$/;

/** Matches combo: corr_{joint1}_{action1}_{joint2}_{action2}_{side}. */
const COMBO_REGEX =
  /^corr_([a-z0-9]+)_([a-z0-9]+)_([a-z0-9]+)_([a-z0-9]+)_([lr])$/;

/**
 * Scan a GLB's morphTargetDictionary for corrective shape keys and build
 * bindings. Returns an empty array if none are found (zero cost at runtime).
 *
 * @param morphTargetDictionary - Morph target name → index map from the GLB
 * @param suppressedGroupKeys - Optional set of groupKeys to exclude (user-deleted GLB correctives)
 */
export function discoverCorrectiveBindings(
  morphTargetDictionary: Record<string, number> | undefined,
  suppressedGroupKeys?: ReadonlySet<string>,
): CorrectiveBinding[] {
  if (!morphTargetDictionary) {
    return [];
  }

  const bindings: CorrectiveBinding[] = [];

  for (const [name, index] of Object.entries(morphTargetDictionary)) {
    // Try single-joint regex first
    const match = name.match(CORR_REGEX);
    if (match) {
      const joint = match[1];
      const side = match[2];
      const action = match[3];
      const keyframeAngleDeg = match[4] ? parseInt(match[4], 10) : null;

      const jointDefs = JOINT_ACTION_MAP[joint];
      if (!jointDefs) {
        continue;
      }

      const def = jointDefs[action];
      if (!def) {
        continue;
      }

      const boneSuffix = SIDE_MAP[side];
      if (!boneSuffix) continue;

      // Unsided (center) bones use the bare name — no L/R suffix
      const boneName = def.unsided
        ? def.bone
        : makeBoneName(def.bone, boneSuffix);

      // Right-side bones use the mirrored quaternion (negate Y and Z).
      // Unsided center bones use the rotation as-is for both L and R morphs.
      const targetRotation: Quat4 =
        side === "r" && !def.unsided
          ? mirrorQuaternion(def.rotation)
          : def.rotation;

      const activationAngle = def.activationAngle;

      // Keyframe angle: if _k{angle} suffix present, use that; otherwise use
      // the full activation angle from the joint definition
      const keyframeAngle =
        keyframeAngleDeg !== null ? deg(keyframeAngleDeg) : activationAngle;

      // Group key includes joint+side+action to keep L/R morph targets separate
      // even when they read from the same unsided bone
      const groupKey = `${joint}:${side}:${action}`;

      // Skip suppressed (user-deleted) correctives
      if (suppressedGroupKeys?.has(groupKey)) continue;

      bindings.push({
        morphName: name,
        morphIndex: index,
        boneName,
        targetRotation,
        activationAngle,
        keyframeAngle,
        groupKey,
      });
      continue;
    }

    // Try combo regex: corr_{joint1}_{action1}_{joint2}_{action2}_{side}
    const comboMatch = name.match(COMBO_REGEX);
    if (comboMatch) {
      const joint1 = comboMatch[1];
      const action1 = comboMatch[2];
      const joint2 = comboMatch[3];
      const action2 = comboMatch[4];
      const side = comboMatch[5];

      const comboKey = `${joint1}_${action1}_${joint2}_${action2}`;
      const comboDef = COMBO_MAP[comboKey];
      if (!comboDef || comboDef.bones.length < 2) continue;

      const boneSuffix = SIDE_MAP[side];
      if (!boneSuffix) continue;

      // Primary bone is the first in the combo definition
      const primaryBone = comboDef.bones[0];
      const primaryBoneName = primaryBone.unsided
        ? primaryBone.bone
        : makeBoneName(primaryBone.bone, boneSuffix);
      const primaryRotation: Quat4 =
        side === "r" && !primaryBone.unsided
          ? mirrorQuaternion(primaryBone.rotation)
          : primaryBone.rotation;

      // Build secondary bone targets
      const secondaryBones: SecondaryBoneTarget[] = comboDef.bones
        .slice(1)
        .map((b) => {
          const secBoneName = b.unsided
            ? b.bone
            : makeBoneName(b.bone, boneSuffix);
          const secRotation: Quat4 =
            side === "r" && !b.unsided
              ? mirrorQuaternion(b.rotation)
              : b.rotation;
          return {
            boneName: secBoneName,
            targetRotation: secRotation,
            activationAngle: b.activationAngle,
          };
        });

      const groupKey = `combo:${comboKey}:${side}`;

      if (suppressedGroupKeys?.has(groupKey)) continue;

      bindings.push({
        morphName: name,
        morphIndex: index,
        boneName: primaryBoneName,
        targetRotation: primaryRotation,
        activationAngle: primaryBone.activationAngle,
        keyframeAngle: primaryBone.activationAngle,
        groupKey,
        secondaryBones,
      });
    }
  }

  // Merge runtime bindings (from the corrective editor)
  if (_runtimeBindings.length > 0) {
    bindings.push(..._runtimeBindings);
  }

  return bindings;
}

// ---------------------------------------------------------------------------
// Definitions for debug panel enumeration
// ---------------------------------------------------------------------------

/** A corrective definition as authored in the bindings JSON. */
export interface CorrectiveDefinition {
  joint: string;
  action: string;
  bone: string;
  targetRotation: Quat4;
  activationAngle: number;
  unsided: boolean;
}

/**
 * Returns all corrective definitions from the bindings JSON, grouped by joint.
 * Each entry represents one joint/action pair with its bone target and rotation.
 */
export function getCorrectiveDefinitions(): CorrectiveDefinition[] {
  const defs: CorrectiveDefinition[] = [];
  for (const [joint, actions] of Object.entries(JOINT_ACTION_MAP)) {
    for (const [action, def] of Object.entries(actions)) {
      defs.push({
        joint,
        action,
        bone: def.bone,
        targetRotation: def.rotation,
        activationAngle: def.activationAngle,
        unsided: !!def.unsided,
      });
    }
  }
  return defs;
}

/**
 * Returns all joint names and actions currently supported.
 * Useful for documentation and validation.
 */
export function getSupportedCorrectiveNames(): string[] {
  const names: string[] = [];
  for (const [joint, actions] of Object.entries(JOINT_ACTION_MAP)) {
    for (const action of Object.keys(actions)) {
      for (const side of Object.keys(SIDE_MAP)) {
        names.push(`corr_${joint}_${side}_${action}`);
      }
    }
  }
  for (const comboKey of Object.keys(COMBO_MAP)) {
    for (const side of Object.keys(SIDE_MAP)) {
      names.push(`corr_${comboKey}_${side}`);
    }
  }
  return names;
}

// ---------------------------------------------------------------------------
// Group bindings for piecewise-linear interpolation
// ---------------------------------------------------------------------------

/** Cache grouped bindings to avoid recomputing every frame. */
const groupCache = new WeakMap<
  readonly CorrectiveBinding[],
  Map<string, CorrectiveGroup>
>();

/**
 * Group corrective bindings by their groupKey for piecewise interpolation.
 * Single-keyframe groups behave identically to simple linear ramp.
 * Result is cached per bindings array reference.
 */
function groupBindings(
  bindings: readonly CorrectiveBinding[],
): Map<string, CorrectiveGroup> {
  const cached = groupCache.get(bindings);
  if (cached) return cached;
  const groups = new Map<string, CorrectiveGroup>();

  for (const binding of bindings) {
    let group = groups.get(binding.groupKey);
    if (!group) {
      group = {
        boneName: binding.boneName,
        targetRotation: binding.targetRotation,
        activationAngle: binding.activationAngle,
        keyframes: [],
        secondaryBones: binding.secondaryBones,
      };
      groups.set(binding.groupKey, group);
    }
    group.keyframes.push({
      morphIndex: binding.morphIndex,
      angle: binding.keyframeAngle,
    });
  }

  // Sort keyframes by angle within each group
  for (const group of groups.values()) {
    group.keyframes.sort((a, b) => a.angle - b.angle);
  }

  groupCache.set(bindings, groups);
  return groups;
}

// ---------------------------------------------------------------------------
// Weight computation
// ---------------------------------------------------------------------------

/**
 * Compute a bone's progress toward a target rotation (0–activationAngle range).
 *
 * Uses swing-twist decomposition to measure the bone's rotation around the
 * target's axis, matching the Blender addon's Euler-based parameterization.
 * This is critical for multi-keyframe correctives: intermediate keyframes
 * store incremental deltas sculpted with the primary at a specific weight,
 * so the progress parameterization must match the addon's to place tent
 * function peaks at the correct angles.
 *
 * Previous approach used quaternion geodesic distance, which measures total
 * rotation magnitude. For bones with off-axis components (common with IK
 * solving), geodesic distance diverges from the per-axis angle the addon
 * uses, shifting tent peaks and producing artifacts at intermediate angles.
 *
 * Returns the progressAngle in radians (0 = at rest, activationAngle = at target).
 * Returns -1 if the bone or rest pose is not found.
 */
const _loggedMissing = new Set<string>();

function computeBoneProgress(
  boneName: string,
  targetRotation: Quat4,
  activationAngle: number,
  boneMap: Map<string, THREE.Bone>,
  restPoses: Map<string, Quat4>,
  bonePoses: Map<string, Quat4> | undefined,
): number {
  const bone = boneMap.get(boneName);
  if (!bone) {
    if (!_loggedMissing.has(`bone:${boneName}`)) {
      _loggedMissing.add(`bone:${boneName}`);
    }
    return -1;
  }
  const rest = restPoses.get(boneName);
  if (!rest) {
    if (!_loggedMissing.has(`rest:${boneName}`)) {
      _loggedMissing.add(`rest:${boneName}`);
    }
    return -1;
  }

  const stored = bonePoses?.get(boneName);
  let cx: number, cy: number, cz: number, cw: number;
  if (stored) {
    cx = stored[0];
    cy = stored[1];
    cz = stored[2];
    cw = stored[3];
  } else {
    cx = bone.quaternion.x;
    cy = bone.quaternion.y;
    cz = bone.quaternion.z;
    cw = bone.quaternion.w;
  }

  // Delta from rest: delta = inv(rest) * current
  const rx = -rest[0],
    ry = -rest[1],
    rz = -rest[2],
    rw = rest[3];
  const dx = rw * cx + rx * cw + ry * cz - rz * cy;
  const dy = rw * cy - rx * cz + ry * cw + rz * cx;
  const dz = rw * cz + rx * cy - ry * cx + rz * cw;
  const dw = rw * cw - rx * cx - ry * cy - rz * cz;

  // --- Swing-twist decomposition ---
  // Extract the rotation axis from the target quaternion. The target is a
  // pure-axis rotation exported by the Blender addon (_make_quat), so the
  // vector part points along the driver axis.
  let tx = targetRotation[0],
    ty = targetRotation[1];
  let tz = targetRotation[2],
    tw = targetRotation[3];

  // Ensure target is in positive-w hemisphere (handle quaternion double-cover)
  if (tw < 0) {
    tx = -tx;
    ty = -ty;
    tz = -tz;
    tw = -tw;
  }

  const axLen = Math.sqrt(tx * tx + ty * ty + tz * tz);
  if (axLen < 1e-10) return 0; // Degenerate: target is identity
  const invAxLen = 1 / axLen;
  const ax = tx * invAxLen,
    ay = ty * invAxLen,
    az = tz * invAxLen;

  // Target's twist angle around its own axis (= activationAngle for pure-axis)
  const targetTwist = 2 * Math.atan2(axLen, tw);
  if (targetTwist < 1e-10) return 0;

  // Project the delta quaternion's vector part onto the target axis.
  // This is the twist component — the rotation around the driver axis,
  // ignoring any orthogonal (swing) rotation.
  const twistDot = dx * ax + dy * ay + dz * az;

  // Signed twist angle: positive = same direction as target, negative = opposite.
  // atan2(projected_vector_component, w) gives the half-angle; multiply by 2.
  // No normalization needed — atan2 handles the ratio correctly.
  const twistAngle = 2 * Math.atan2(twistDot, dw);

  // Express progress as a proportion of the activation arc, scaled to
  // [0, activationAngle]. This matches the addon's parameterization:
  //   current_angle = t * full_angle_deg
  // where t = twist_progress / target_twist.
  return Math.max(
    0,
    Math.min(activationAngle, (twistAngle / targetTwist) * activationAngle),
  );
}

/**
 * Compute corrective shape key weights from current bone rotations using
 * pose-space deformation (quaternion geodesic distance).
 *
 * For each corrective group, computes the quaternion distance from the bone's
 * current delta rotation to the group's target rotation, then derives a weight
 * using piecewise-linear interpolation across keyframes.
 *
 * Combo correctives use product activation: the weight is the product of all
 * participating bones' individual progress ratios.
 */
export function computeCorrectiveWeights(
  bindings: readonly CorrectiveBinding[],
  boneMap: Map<string, THREE.Bone>,
  restPoses: Map<string, [number, number, number, number]>,
  bonePoses: Map<string, [number, number, number, number]> | undefined,
): Map<number, number> {
  const weights = new Map<number, number>();

  // Group bindings for piecewise interpolation
  const groups = groupBindings(bindings);

  for (const group of groups.values()) {
    // Compute primary bone progress using shared helper
    const progressAngle = computeBoneProgress(
      group.boneName,
      group.targetRotation,
      group.activationAngle,
      boneMap,
      restPoses,
      bonePoses,
    );
    if (progressAngle < 0) {
      for (const kf of group.keyframes) weights.set(kf.morphIndex, 0);
      continue;
    }

    // Combo correctives: product of all bone progress ratios
    if (group.secondaryBones && group.secondaryBones.length > 0) {
      let comboFactor =
        group.activationAngle > 0
          ? Math.min(1, progressAngle / group.activationAngle)
          : 0;

      for (const sec of group.secondaryBones) {
        const secProgress = computeBoneProgress(
          sec.boneName,
          sec.targetRotation,
          sec.activationAngle,
          boneMap,
          restPoses,
          bonePoses,
        );
        if (secProgress < 0) {
          comboFactor = 0;
          break;
        }
        comboFactor *=
          sec.activationAngle > 0
            ? Math.min(1, secProgress / sec.activationAngle)
            : 0;
      }

      // Combo correctives are single-point (no multi-keyframe arc)
      for (const kf of group.keyframes) {
        weights.set(kf.morphIndex, comboFactor);
      }
      continue;
    }

    const keyframes = group.keyframes;

    if (keyframes.length === 1) {
      // Single keyframe: simple linear ramp
      const jointWeight =
        keyframes[0].angle > 0
          ? Math.min(1, progressAngle / keyframes[0].angle)
          : 0;
      weights.set(keyframes[0].morphIndex, jointWeight);
      continue;
    }

    // Multi-keyframe: primary (last/max-angle) uses a linear ramp;
    // intermediates are additive course-corrections with tent weights.
    // Intermediate deltas are INCREMENTAL (small fixes on top of the
    // primary's contribution), so rest-space mismatch at nearby angles
    // is negligible.

    // Zero all first
    for (const kf of keyframes) {
      weights.set(kf.morphIndex, 0);
    }

    if (progressAngle <= 0) continue;

    // Primary: linear ramp (always active)
    const primary = keyframes[keyframes.length - 1];
    const primaryWeight =
      primary.angle > 0 ? Math.min(1, progressAngle / primary.angle) : 0;
    weights.set(primary.morphIndex, primaryWeight);

    // Intermediates: tent functions (additive)
    for (let i = 0; i < keyframes.length - 1; i++) {
      const kf = keyframes[i];
      const leftAngle = i > 0 ? keyframes[i - 1].angle : 0;
      const rightAngle = keyframes[i + 1].angle;
      const kfAngle = kf.angle;

      let w = 0;
      if (progressAngle > leftAngle && progressAngle < rightAngle) {
        if (progressAngle <= kfAngle) {
          const span = kfAngle - leftAngle;
          w = span > 0 ? (progressAngle - leftAngle) / span : 0;
        } else {
          const span = rightAngle - kfAngle;
          w = span > 0 ? (rightAngle - progressAngle) / span : 0;
        }
      }
      weights.set(kf.morphIndex, w);
    }
  }

  return weights;
}

/**
 * Compute the theoretical max corrective weight for a bone at a given delta
 * quaternion, using the loaded binding definitions. Used by the debug overlay
 * to show what percentage of the corrective shape key would be applied, even
 * when no actual morph targets exist in the model.
 *
 * @param boneName - Full bone name (e.g., "shinL")
 * @param dx - Delta quaternion x
 * @param dy - Delta quaternion y
 * @param dz - Delta quaternion z
 * @param dw - Delta quaternion w
 */
export function computeTheoreticalMaxWeight(
  boneName: string,
  dx: number,
  dy: number,
  dz: number,
  dw: number,
): number {
  // Strip side suffix (L/R) to get base bone name for lookup
  const last = boneName.charAt(boneName.length - 1);
  const isSided = last === "L" || last === "R";
  const baseBone = isSided ? boneName.slice(0, -1) : boneName;
  const isRight = last === "R";

  let maxWeight = 0;

  for (const actions of Object.values(JOINT_ACTION_MAP)) {
    for (const def of Object.values(actions)) {
      if (def.bone !== baseBone) continue;

      // For right-side bones on sided correctives, mirror the target
      const target =
        isRight && !def.unsided ? mirrorQuaternion(def.rotation) : def.rotation;

      const dToTarget = quaternionAngle(
        dx,
        dy,
        dz,
        dw,
        target[0],
        target[1],
        target[2],
        target[3],
      );
      const progressAngle = Math.max(0, def.activationAngle - dToTarget);
      const w =
        def.activationAngle > 0
          ? Math.min(1, progressAngle / def.activationAngle)
          : 0;
      if (w > maxWeight) maxWeight = w;
    }
  }

  return maxWeight;
}

/**
 * Apply computed corrective weights to a mesh's morph target influences.
 * Writes directly to the influences array for zero-allocation per-frame updates.
 */
export function applyCorrectiveInfluences(
  influences: number[],
  weights: Map<number, number>,
): void {
  for (const [index, weight] of weights) {
    influences[index] = weight;
  }
}
